import { FC, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Alert, Autocomplete, Checkbox, FormControlLabel, InputAdornment, MenuItem, Stack, TextField, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { FeatureModule, InternalPackage, InternalRole } from '../../types/Plans'
import {
  addRoleNames,
  getPackageNameError,
  getWholeNumberError,
  packageTierLabelKeys,
  packageTiers,
  packageTypeLabelKeys,
  packageTypes,
  PackageFormValues,
  sortRoleNames,
  toPackageFormValues,
} from '../helpers/planHelpers'

type PackageEditDialogProps = {
  open: boolean
  planPackage?: InternalPackage
  packages: InternalPackage[]
  roles: InternalRole[]
  modules: FeatureModule[]
  isSaving?: boolean
  onClose: () => void
  onSave: (values: PackageFormValues) => void
}

type NumberField = 'price' | 'maxGameSlots' | 'maxFollowedGames'

const numberFields: { name: NumberField; labelKey: string }[] = [
  { name: 'price', labelKey: 'internal-csm:plans_price' },
  { name: 'maxGameSlots', labelKey: 'internal-csm:plans_game_slots' },
  { name: 'maxFollowedGames', labelKey: 'internal-csm:plans_followed_games' },
]

export const PackageEditDialog: FC<PackageEditDialogProps> = ({ open, planPackage, packages, roles, modules, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, formState, getValues, setValue } = useForm<PackageFormValues>({
    mode: 'onChange',
    defaultValues: toPackageFormValues(planPackage),
  })
  const [moduleConfirm, setModuleConfirm] = useState<ConfirmDialogData<FeatureModule>>()

  useEffect(() => {
    if (open) reset(toPackageFormValues(planPackage))
  }, [open, planPackage, reset])

  const roleOptions = useMemo(() => sortRoleNames(roles.map(({ name }) => name)), [roles])

  const askToAddModule = (featureModule: FeatureModule) => {
    const added = sortRoleNames(featureModule.roles.filter((role) => !getValues('roles').includes(role)))
    setModuleConfirm({
      title: t('internal-csm:plans_add_module_roles'),
      confirmText: (
        <Stack spacing={1}>
          <Typography>{t('internal-csm:plans_add_module_roles_confirm', { name: featureModule.name })}</Typography>
          <Typography fontWeight="bold">{added.length ? added.join(', ') : t('internal-csm:plans_add_module_roles_none')}</Typography>
        </Stack>
      ),
      actionText: t('internal-csm:plans_add_roles'),
      data: featureModule,
    })
  }

  const handleModuleConfirm = (confirmed?: ConfirmDialogData<FeatureModule>) => {
    setModuleConfirm(undefined)
    const featureModule = confirmed?.data
    if (featureModule) setValue('roles', addRoleNames(getValues('roles'), featureModule.roles), { shouldDirty: true, shouldValidate: true })
  }

  const submit = handleSubmit(onSave)
  const canSave = formState.isValid && (!planPackage || formState.isDirty) && !isSaving

  return (
    <>
      <InternalFormDialog
        open={open}
        title={planPackage ? t('internal-csm:plans_edit_package') : t('internal-csm:plans_new_package')}
        onClose={onClose}
        onSave={submit}
        saveDisabled={!canSave}
        maxWidth="md"
      >
        <Stack spacing={2} pt={1}>
          {planPackage && <Alert severity="warning">{t('internal-csm:plans_package_update_warning')}</Alert>}
          <Controller
            name="name"
            control={control}
            rules={{
              validate: (name) => {
                const error = getPackageNameError(name, packages, planPackage?.id)
                return error ? t(`internal-csm:plans_name_${error}`) : true
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                size="small"
                label={t('internal-csm:plans_package_name')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
              />
            )}
          />
          <Stack direction="row" spacing={2}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <TextField {...field} select size="small" label={t('internal-csm:plans_type')} sx={{ flex: 1 }}>
                  {packageTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(packageTypeLabelKeys[type])}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="tier"
              control={control}
              render={({ field }) => (
                <TextField {...field} select size="small" label={t('internal-csm:plans_tier')} sx={{ flex: 1 }}>
                  {packageTiers.map((tier) => (
                    <MenuItem key={tier} value={tier}>
                      {t(packageTierLabelKeys[tier])}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
          <Controller
            name="description"
            control={control}
            render={({ field }) => <TextField {...field} size="small" label={t('internal-csm:plans_description')} />}
          />
          <Stack direction="row" spacing={2}>
            {numberFields.map(({ name, labelKey }) => (
              <Controller
                key={name}
                name={name}
                control={control}
                rules={{
                  validate: (value) => {
                    const error = getWholeNumberError(value)
                    return error ? t(`internal-csm:plans_number_${error}`) : true
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    size="small"
                    label={t(labelKey)}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    inputProps={{ inputMode: 'numeric' }}
                    InputProps={name === 'price' ? { endAdornment: <InputAdornment position="end">€</InputAdornment> } : undefined}
                    required
                    sx={{ flex: 1 }}
                  />
                )}
              />
            ))}
          </Stack>
          <Controller
            name="roles"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                filterSelectedOptions
                options={roleOptions}
                value={field.value}
                onChange={(_, value) => field.onChange(value)}
                renderInput={(params) => <TextField {...params} label={t('internal-csm:plans_roles')} placeholder={t('internal-csm:plans_add_role')} />}
              />
            )}
          />
          <TextField
            select
            size="small"
            label={t('internal-csm:plans_add_module_roles')}
            value=""
            onChange={(event) => {
              const featureModule = modules.find(({ id }) => id === event.target.value)
              if (featureModule) askToAddModule(featureModule)
            }}
            disabled={!modules.length}
          >
            {modules.map((featureModule) => (
              <MenuItem key={featureModule.id} value={featureModule.id}>
                {featureModule.name}
              </MenuItem>
            ))}
          </TextField>
          <Controller
            name="visible"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
                label={t('internal-csm:plans_visible')}
              />
            )}
          />
        </Stack>
      </InternalFormDialog>
      <ConfirmDialog open={!!moduleConfirm} confirmDialogData={moduleConfirm} onClose={handleModuleConfirm} />
    </>
  )
}
